export default Debounce = (() => {
  // Store active debounced functions for cleanup if needed
  const activeTimeouts = new Map();

  /**
   * Create a debounced version of a function
   * @param {function} func - The function to debounce
   * @param {number} wait - Time in milliseconds to delay
   * @returns {function} - Debounced function
   */
  const create = (func, wait) => {
    if (typeof func !== "function") {
      throw new Error("Expected a function as the first argument.");
    }
    if (typeof wait !== "number" || wait < 0) {
      throw new Error("Expected a non-negative number as the second argument.");
    }

    let timeout;

    const debounced = (...args) => {
      // Clear the previous timeout if it exists
      clearTimeout(timeout);

      // Set a new timeout for the given wait period
      timeout = setTimeout(() => {
        func(...args);
      }, wait);

      // Track the timeout reference for potential cleanup
      activeTimeouts.set(debounced, timeout);
    };

    return debounced;
  };

  /**
   * Cancel an active debounced function
   * @param {function} debouncedFunc - The debounced function to cancel
   */
  const cancel = (debouncedFunc) => {
    const timeout = activeTimeouts.get(debouncedFunc);
    if (timeout) {
      clearTimeout(timeout);
      activeTimeouts.delete(debouncedFunc);
    }
  };

  /**
   * Cancel all active debounced functions
   */
  const cancelAll = () => {
    activeTimeouts.forEach((timeout) => clearTimeout(timeout));
    activeTimeouts.clear();
  };

  return {
    create,
    cancel,
    cancelAll,
  };
})();